import React from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, Flex } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link.tsx';

import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import HelpPanelButton from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { JswShareButton } from '@atlassian/jira-software-report-header-common/src/ui/share-button/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { NonCriticalErrorBoundary } from '../../common/utils/non-critical-error-boundary.tsx';
import { FeedbackButton } from './feedback-button/index.tsx';
import messages from './messages.tsx';

const Header = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const breadcrumbs = <BreadcrumbRenderer />;

	const [{ match }] = useRouter();
	const boardId = match.params.boardId ?? '';

	return (
		<PageHeader breadcrumbs={breadcrumbs} truncateTitle disableTitleStyles>
			<HeaderTopContainer>
				<TitleContainer>
					<Heading size="large" as="h1">
						{formatMessage(messages.title)}
					</Heading>
				</TitleContainer>
				<NonCriticalErrorBoundary fallback="unmount" eventLocation="feedbackButton">
					<Flex>
						<div>
							<FeedbackButton />
						</div>
						<Box paddingInlineStart="space.200">
							<JswShareButton boardId={boardId} />
						</Box>
					</Flex>
				</NonCriticalErrorBoundary>
			</HeaderTopContainer>
			<HeaderContent>
				<PageDescription>
					{formatMessage(messages.description)}{' '}
					<HelpPanelButton
						articleId="7lw1NsGKeAt0977kCLMITV"
						appearance="link"
						spacing="none"
						onClick={() => {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'cycleTimeLearnMore',
								}),
							);
						}}
						fallbackComponent={
							<Link
								href="https://support.atlassian.com/jira-software-cloud/docs/strategies-to-reduce-your-cycle-time/"
								target="_blank"
								onClick={() => {
									fireUIAnalytics(
										createAnalyticsEvent({
											action: 'clicked',
											actionSubject: 'cycleTimeLearnMore',
										}),
									);
								}}
							>
								{formatMessage(messages.learnMore)}
							</Link>
						}
					>
						{formatMessage(messages.learnMore)}
					</HelpPanelButton>
				</PageDescription>
			</HeaderContent>
		</PageHeader>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderTopContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 4}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageDescription = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N600),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContent = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	margin: `${token('space.200', '16px')} 0`,
});

export default Header;
